/**
 * Sim — the Pixi-FREE deterministic simulation core (Phase C extraction).
 *
 * This is the ONE sim step, extracted verbatim from `World.update` (+ its private passes) with
 * every RENDER call routed to a {@link SimFx} sink. `World` HAS-A `Sim`, supplies a `WorldFx`,
 * and renders from `Sim`'s public state; the headless proofs (`src/levels/sim.ts` wraps this),
 * the solver, and the server re-sim all drive the SAME core with `NOOP_FX`. Before this, the
 * step lived inside a 1296-line Pixi-coupled `World` and was hand-MIRRORED in three other places
 * (HANDOFF_SIM_EXTRACTION §1) — this file kills the duplication.
 *
 * Determinism (HARD): under src/engine/ → the ESLint guard bans transcendentals / wall-clock /
 * RNG here. The sim already obeyed it inside World; keep it. Render-only math (juice/particles,
 * the coin-chain pitch) stays in `World`/`WorldFx` where the lint doesn't reach the sim path.
 *
 * Death POLICY vs DETECTION (§5): `Sim` DETECTS death (records the cause) and, per an owner-set
 * `deathPolicy`, either respawns in place (the live game) or stays terminal (solver/proof read
 * `dead` and abort; a one-life World freezes via `fx.onFreeze`). The respawn is synchronous at
 * the death site — exactly where `World.die()` did it — so the extraction is BIT-IDENTICAL to
 * the shipped step (a post-step owner respawn would diverge on the enemy-death-mid-overlap frame,
 * where the rest of the shipped step already ran on respawned state). The owner still owns the
 * policy; only the mechanism is fixed for bit-parity.
 */
import { Player } from './Player';
import { type AABB, TileGrid } from './TileGrid';
import { PLAYER_AVATAR, resolveForm, type ResolvedForm } from './forms';
import { mergeConfig } from './PhysicsConfig';
import { type ArchetypeDef, resolveArchetype } from './Archetype';
import { spawnEntity, spawnEntityAtPx } from './entities/spawnEntity';
import type { Entity, EntityContext } from './Entity';
import { resolvePlayerVsSolid } from './solidResolve';
import { resolveAttack, isAttackKillable } from './combat';
import { PROJECTILE_H } from './entities/Projectile';
import { type ClearCondition, EntityType, type LevelFormat } from './LevelFormat';
import {
  type ClearProgress,
  DEFAULT_CONDITION,
  KILLABLE_ENEMY_TYPES,
  conditionHint,
  conditionSatisfied,
  countCoins,
  countKillableEnemies,
  secToSteps,
  timerExpired,
} from './clearConditions';
import { Rng } from './Rng';
import { type ActiveMutators, NO_MUTATORS } from './Mutators';
import { applyAddMaxHeart, applyHeal, FULL_HEART_SCORE_BONUS, grantInvuln, tickInvuln } from './buffs';
import type { InputSnapshot } from './Input';
import type { DeathCause } from './deathCell';

/** Seconds of damage immunity (i-frames) after a hit. Matches the value World shipped. */
const IFRAME_SECONDS = 1.2;
/** Fixed steps a warp is inert after firing (swallows the destination re-trigger). */
const WARP_COOLDOWN_STEPS = 12;
/** POLISH_03 #8: respawn mercy window after a DEATH respawn. Granted in die() AFTER respawn()
 *  returns — die() is the only death discriminator, so World.load's initial respawn() and
 *  manual resets (R / retry / skin swap) stay byte-identical (no grant). Step-keyed integer →
 *  deterministic on replay. Also blocks the same-step re-hit from the overlap pass that runs
 *  after a synchronous mid-step respawn. */
const RESPAWN_INVULN_STEPS = 45;
/** POLISH_03 #5: cadence (fixed steps) at which Sim.entities is compacted (swap-removing dead,
 *  no-longer-visible entities) — an integer step-keyed modulo, so it fires on the SAME step
 *  every replay regardless of wall-clock (the determinism contract every other counter here
 *  already obeys). One second @ 60Hz is frequent enough to bound unbounded growth from
 *  long-running autofire/spawner sessions without compacting so often it's wasted work. */
const COMPACT_CADENCE_STEPS = 60;
/** RUN-AND-GUN: px the muzzle sits IN FRONT of the body center along the aim (gun barrel). */
const MUZZLE_OFFSET = 30;

/**
 * The render sink. Every call here is a cosmetic side effect the sim EMITS but never READS —
 * so it can never perturb replay parity. `World` provides a `WorldFx` that drives
 * juice/particles/filters/sound + the sprite/camera; headless consumers use {@link NOOP_FX}.
 * (x/y are world px. Hooks are named after the sim event, not the effect, so the owner decides
 * the effect.)
 */
export interface SimFx {
  /** Player jumped (takeoff sfx + squash + dust). */
  onJump(): void;
  /** Player landed; `impactVy` is +down px/s at contact (fall-scaled squash + dust + thud). */
  onLand(impactVy: number): void;
  /** Player head-bumped a ceiling/block (bump sfx). */
  onBump(): void;
  /** A coin was collected this step; `stepCount` keys the render-only coin-chain arpeggio. */
  onCoin(stepCount: number): void;
  /** An entity was killed at (x,y) (kill punch: trauma/hitstop/burst/shockwave). */
  onKill(x: number, y: number): void;
  /** The player took a hit (hurt sfx + flash + shake + chromatic split). */
  onHurt(): void;
  /** A death occurred at (x,y) with `cause` (death juice + the analytics/heatmap report). */
  onDeath(cause: DeathCause, x: number, y: number): void;
  /** After a death→respawn, the red splatter at the respawn point (x,y). */
  onRespawnSplat(x: number, y: number): void;
  /** A death under a no-respawn policy (one-life): red splatter at (x,y) + enter the fail state. */
  onFreeze(x: number, y: number): void;
  /** A respawn happened; the owner resets render state + re-syncs sprites, camera at (cx,feetY). */
  onRespawn(cx: number, feetY: number): void;
  /** The level was cleared (win banner + jingle). */
  onClear(): void;
  /** A warp fired (teleport sfx + camera snap to the destination). */
  onWarp(): void;
  /** An entity was emitted into the live list this step (the owner adds a parallel sprite). */
  onSpawn(e: Entity): void;
  /** Proxy to the sfx player (entities route their own audio here). */
  playSound(key: string, volume?: number, pitch?: number): void;
  /** OPTIONAL render-only juice emit (particles/floating text). Callers use `?.`. */
  emitFx?(
    kind: 'sparkle' | 'pop' | 'burst' | 'dust',
    x: number, y: number,
    opts?: { text?: string; color?: number; count?: number; dir?: number },
  ): void;
  /** OPTIONAL — a dash armed this step; `dir` is the horizontal sign (+1/-1). Cosmetic
   *  (whoosh + trauma + trailing dust). Carries no sim state → replay-safe. Callers use `?.`. */
  onDash?(dir: number): void;
  /** OPTIONAL — the player crossed the false→true edge of the SKID (turn-around brake) state
   *  this step (screech + dust). Cosmetic; callers use `?.`. */
  onSkid?(): void;
  /** OPTIONAL — a launchpad/bumper flung the player along unit-ish (dx,dy); World squashes
   *  along the fling axis + adds a little trauma. Cosmetic; callers use `?.`. */
  onLaunch?(dx: number, dy: number): void;
}

/** A headless render sink — every hook a no-op. Used by the solver / proof / server re-sim. */
export const NOOP_FX: SimFx = {
  onJump() { /* headless */ }, onLand() { /* headless */ }, onBump() { /* headless */ },
  onCoin() { /* headless */ }, onKill() { /* headless */ }, onHurt() { /* headless */ },
  onDeath() { /* headless */ }, onRespawnSplat() { /* headless */ }, onFreeze() { /* headless */ },
  onRespawn() { /* headless */ }, onClear() { /* headless */ }, onWarp() { /* headless */ },
  onSpawn() { /* headless */ }, playSound() { /* headless */ },
};

/** How a death resolves. `respawn` = rebuild + reset in place (the live game / a normal death).
 *  `terminal` = record + stop (a solver/proof aborts the branch; a one-life World freezes). */
export type DeathPolicy = 'respawn' | 'terminal';

export interface SimOptions {
  /** Archetype id (default: `level.meta.archetype`, degrading to classic). */
  archetype?: unknown;
  /** Active rule-variant category (default: vanilla). */
  mutators?: ActiveMutators;
  /** Render sink (default: {@link NOOP_FX}). */
  fx?: SimFx;
  /** Death resolution (default: `terminal` — the headless-consumer default; World sets `respawn`
   *  for the live game, `terminal` under the one-life mutator so `fx.onFreeze` can end the run). */
  deathPolicy?: DeathPolicy;
  /**
   * POLISH_03 #5 (entity-compaction sub-fix): opt-in, default FALSE. When true, `step()`
   * periodically swap-removes dead/no-longer-visible entities from `Sim.entities` (see
   * {@link compactDeadEntities}) so a long-running session with a SPAWNER/autofire can't
   * accrete unbounded dead entities. Defaults false (byte-identical to pre-fix behaviour)
   * because the golden fixture (`Sim.golden.test.ts`/`golden.test.ts`, GOLDEN GUARD-protected)
   * bakes in "a dead entity stays in Sim.entities forever" — World never passes `true`, so
   * this is inert for the live game / golden suite today; a caller that runs long sessions
   * (or a future fixture re-bake) can opt in explicitly.
   */
  compactEntities?: boolean;
}

/**
 * The deterministic sim core. Construct, then either drive `step(dt,input)` each fixed 60 Hz
 * frame (World / server) or read state after each step. Owns sim state + `step()` + `respawn()`;
 * knows nothing about Pixi.
 */
export class Sim {
  readonly grid: TileGrid;
  readonly player: Player;
  readonly archetype: ArchetypeDef;
  readonly mutators: ActiveMutators;
  /** Stable per-level RNG seed (integer math only). Recorded into replays for forward-safety. */
  readonly initialSeed: number;
  /** Blessed deterministic RNG (seeded off the level). Handed to entities via ctx; the sim
   *  itself draws none today. */
  readonly rng: Rng;

  /** Set once the player touches the goal with the clear condition satisfied. */
  cleared = false;
  /** Set the frame a death is detected. Under `respawn` policy it is cleared again by the
   *  in-step respawn (so it is only ever observably true to a `terminal`-policy owner). */
  dead = false;
  deathCause: DeathCause | null = null;

  hearts: number;
  maxHearts: number;
  coins = 0;
  /** POLISH_03 #4: coins collected from PLACED COIN entities only (excludes a runtime-spawned
   *  QBlock/Brick reward coin) — the `collect_all_coins` gate reads THIS, not `coins`, so that
   *  condition can't be farmed by bonking an infinite coin block. `coins` (unrestricted) still
   *  feeds `reach_goal_with_coins`, where a bonus coin legitimately counts toward `n`. */
  placedCoinsCollected = 0;
  score = 0;
  enemiesKilled = 0;
  iframeTimer = 0;
  invulnSteps = 0;
  stepCount = 0;
  /** Cumulative deaths this Sim session (HUD "attempts"); bumped in die(), never reset by respawn. */
  deaths = 0;
  /** Increments on every respawn() — the loop owner watches it to restart recorder + ghost. */
  respawnGen = 0;
  /** When the player is at the flag with the condition UNMET, the hint to surface. */
  goalHint = '';

  readonly clearCondition: ClearCondition;
  readonly totalCoins: number;
  readonly totalEnemies: number;
  readonly timeLimitSteps: number;

  /** The live simulated entities. Public so the renderer can position a parallel sprite array;
   *  reassigned fresh on every (re)build (respawn) — the owner re-syncs its sprites via fx. */
  entities: Entity[] = [];

  private readonly level: LevelFormat;
  private readonly ts: number;
  private readonly ctx: EntityContext;
  private readonly fx: SimFx;
  private readonly deathPolicy: DeathPolicy;
  /** POLISH_03 #5 — see {@link SimOptions.compactEntities}. */
  private readonly compactEntities: boolean;
  /**
   * RUN-AND-GUN §3-gate: "can the player deal damage to enemies AT ALL?" = archetype.offense
   * OR the `shoot` verb. Gates ctx.killEnemiesInBox so a bullet kills under `offense:false`
   * (the pure-shooter rule) while the melee path (resolveAttackHitbox) still gates on
   * offenseEnabled. false for every non-offense, non-shoot archetype (PRECISION/DASH/MOMENTUM/
   * AUTO-RUNNER) → their would-be bullet (which never spawns) can't kill either. Constant for
   * the level's lifetime.
   */
  private readonly allowEnemyKills: boolean;
  private pendingSpawns: Entity[] = [];
  private readonly latches = new Map<number, boolean>();
  private rider: Entity | null = null;
  private warpPoints: { linkId: number; cx: number; feetY: number }[] = [];
  private warpCooldownSteps = 0;
  private pendingKill = false;
  /** POLISH_03 #10: true when the pending kill came from a `starBlocks` caller (MINE/LASER) —
   *  consulted (and reset) alongside `pendingKill` at end-of-step so an active star can downgrade
   *  it to a no-op without touching the lethal LAVA/THWOMP-crush/pit callers (which never set it). */
  private pendingKillStarBlocks = false;
  private respawnCx = 0;
  private respawnFeetY = 0;
  /** The level's PLAYER_START (captured once in buildStatics). `atStartRespawn` compares the
   *  live respawn point against this to tell a from-start attempt from a from-checkpoint one. */
  private startCx = 0;
  private startFeetY = 0;
  private goalBox: AABB | null = null;

  constructor(level: LevelFormat, opts: SimOptions = {}) {
    this.level = level;
    this.grid = new TileGrid(level);
    this.ts = this.grid.ts;
    // `opts.archetype` is an id (like the solver/proof pass); World passes `this.archetype.id`.
    this.archetype = resolveArchetype(opts.archetype ?? level.meta.archetype);
    this.mutators = opts.mutators ?? NO_MUTATORS;
    this.fx = opts.fx ?? NOOP_FX;
    this.deathPolicy = opts.deathPolicy ?? 'terminal';
    this.compactEntities = opts.compactEntities ?? false;

    const d = level.dimensions;
    this.initialSeed = (d.widthTiles * 31 + d.heightTiles) * 31 + level.entities.length;
    this.rng = new Rng(this.initialSeed);

    // Player build — mirror World: avatar base config → archetype profile overlay → mutator overlay.
    this.player = new Player(this.resolveActiveForm());
    this.player.wallSlideEnabled = this.archetype.verbs.wallSlide;
    this.player.wallJumpEnabled = this.archetype.verbs.wallJump;
    this.player.attackEnabled = this.archetype.verbs.attack;
    this.player.dashEnabled = this.archetype.verbs.dash;
    this.player.climbEnabled = this.archetype.verbs.climb;
    this.player.shootEnabled = this.archetype.verbs.shoot;
    // §3-gate: a bullet needs the gun (verbs.shoot), a stomp/sword needs offense.
    this.allowEnemyKills = this.archetype.offense || this.archetype.verbs.shoot;

    this.maxHearts = level.meta.startHearts ?? 3;
    this.hearts = this.maxHearts;
    this.clearCondition = level.meta.clearCondition ?? DEFAULT_CONDITION;
    this.timeLimitSteps = secToSteps(level.meta.timeLimitSec);
    this.totalCoins = countCoins(level.entities);
    this.totalEnemies = countKillableEnemies(level.entities);

    this.ctx = {
      grid: this.grid,
      rng: this.rng,
      player: this.player,
      input: { axisX: 0, jumpPressed: false, jumpHeld: false, runActive: false, duck: false, up: false, attack: false, dash: false, grab: false, aimUp: false, fireHeld: false },
      hurtPlayer: () => this.hurtPlayer(),
      addScore: (n) => { this.score += n; },
      addCoin: (countsTowardTotal = true) => {
        this.coins++; // POLISH_03 #4: always bumped — reach_goal_with_coins counts every coin
        if (countsTowardTotal) this.placedCoinsCollected++; // collect_all_coins: placed-only
        this.fx.onCoin(this.stepCount);
      },
      killEntity: (e) => {
        // POLISH_03 #3: a runtime-spawned enemy (a SPAWNER grunt) never tallies toward
        // defeat_all_enemies — totalEnemies counts only PLACED enemies, so farming an infinite
        // spawner must never satisfy the condition in place of killing the authored roster.
        if (e.alive && !e.spawned && KILLABLE_ENEMY_TYPES.has(e.type)) this.enemiesKilled++;
        if (e.alive) this.fx.onKill(e.body.x + e.body.w / 2, e.body.y + e.body.h / 2);
        if (e.onKilled) e.onKilled(); else e.alive = false;
      },
      bouncePlayer: () => { this.player.vy = -this.player.config.jumpVelocity * 0.62; },
      offenseEnabled: this.archetype.offense,
      playSound: (k, v, p) => this.fx.playSound(k, v, p),
      emitFx: (kind, x, y, opts) => this.fx.emitFx?.(kind, x, y, opts),
      launchFx: (dx, dy) => this.fx.onLaunch?.(dx, dy),
      healPlayer: (n) => this.healPlayer(n),
      addMaxHeart: () => this.addMaxHeart(),
      grantInvuln: (steps) => this.grantInvuln(steps),
      killPlayer: (starBlocks = false) => {
        this.pendingKill = true;
        if (starBlocks) this.pendingKillStarBlocks = true; // POLISH_03 #10 — see field doc
      },
      activateCheckpoint: (cx, feetY) => this.activateCheckpoint(cx, feetY),
      spawnEntityAt: (type, cx, feetY, props) => this.spawnEntityAt(type, cx, feetY, props),
      killEnemiesInBox: (box) => this.killEnemiesInBox(box),
      requestWarp: (linkId, srcCx, srcFeetY) => this.requestWarp(linkId, srcCx, srcFeetY),
      setLatch: (id, on) => {
        if ((this.latches.get(id) ?? false) === on) return;
        this.latches.set(id, on);
        if (on) this.fx.playSound('select', 0.55);
      },
      latchOn: (id) => this.latches.get(id) ?? false,
    };

    this.buildStatics();
    this.buildSimEntities();
    this.player.placeFeet(this.respawnCx, this.respawnFeetY);
  }

  // ── Fixed-step update (extracted verbatim from World.update) ─────────────────
  step(dt: number, rawInput: InputSnapshot): void {
    if (this.cleared || this.dead) return;

    // Mirror mutator: flip the control axis at the TOP so every consumer sees it.
    let input = rawInput;
    if (this.mutators.mirror) input = { ...input, axisX: (-input.axisX) as -1 | 0 | 1 };

    // Timer countdown (pure fixed-step). Expiry is the run's death.
    this.stepCount++;
    if (timerExpired(this.timeLimitSteps, this.stepCount)) {
      this.fx.playSound('hurt', 0.5);
      this.die('timeout');
      return;
    }
    this.goalHint = '';

    this.ctx.input = input;
    this.warpCooldownSteps = Math.max(0, this.warpCooldownSteps - 1);

    this.player.update(dt, input, this.grid, {
      onJump: () => this.fx.onJump(),
      onLand: (vy) => this.fx.onLand(vy),
      onBump: () => this.fx.onBump(),
      onShoot: (dx, dy) => this.spawnBullet(dx, dy),
      onDash: (dir) => this.fx.onDash?.(dir),
      onSkid: () => this.fx.onSkid?.(),
    });

    this.iframeTimer = Math.max(0, this.iframeTimer - dt);
    this.invulnSteps = tickInvuln(this.invulnSteps);

    for (const e of this.entities) e.update(dt, this.ctx);
    this.carryRider(dt);
    this.resolveSolidEntities();
    this.resolveAttackHitbox();

    const pb = this.player.body;
    for (const e of this.entities) {
      if (e.alive && overlap(pb, e.body)) e.onPlayerOverlap(this.ctx);
    }

    this.flushSpawns();
    // POLISH_03 #5 (entity compaction): opt-in via SimOptions.compactEntities (default false) —
    // see compactDeadEntities()'s doc for why this can't be unconditional under this MODE (the
    // golden fixture bakes in "a dead entity stays in Sim.entities forever").
    if (this.compactEntities) this.compactDeadEntities();

    if (this.pendingKill) {
      this.pendingKill = false;
      const blocked = this.pendingKillStarBlocks && this.invulnSteps > 0;
      this.pendingKillStarBlocks = false;
      // POLISH_03 #10: an active star downgrades a starBlocks-flagged kill (MINE/LASER) to a
      // no-op — the identical hazard already blocks its instakill:false twin via hurtPlayer's
      // invulnSteps check; this makes the instakill:true variant consistent instead of a
      // player-invisible "sometimes pierces star" trap. LAVA/THWOMP-crush/pit never set the
      // flag, so they stay lethal through star (genre-standard) — untouched by this branch.
      if (!blocked) { this.die('hazard'); return; }
    }

    this.checkGoal();

    if (this.player.body.y > this.grid.heightPx + 240) this.die('pit');
  }

  /** Resolve the single player avatar with the level's archetype + mutator physics layered on. */
  private resolveActiveForm(): ResolvedForm {
    const rf = resolveForm(PLAYER_AVATAR);
    const withArchetype = mergeConfig(rf.config, this.archetype.physics);
    return { ...rf, config: this.mutators.applyOverlay(withArchetype) };
  }

  // ── Death / respawn (policy owned by the constructor's deathPolicy) ──────────
  private die(cause: DeathCause): void {
    if (this.dead) return; // a single step can only die once
    this.dead = true;
    this.deathCause = cause;
    this.deaths++;
    const dcx = this.player.body.x + this.player.body.w / 2;
    const dcy = this.player.body.y + this.player.body.h / 2;
    this.fx.onDeath(cause, dcx, dcy);
    if (this.deathPolicy === 'respawn') {
      this.respawn(); // synchronous, at the death site — bit-identical to the shipped World.die()
      // POLISH_03 #8: death-respawn mercy window. Only here — respawn() itself stays grant-free
      // so the initial World.load call and manual resets are unchanged.
      this.invulnSteps = RESPAWN_INVULN_STEPS;
      const b = this.player.body;
      this.fx.onRespawnSplat(b.x + b.w / 2, b.y + b.h / 2);
    } else {
      this.fx.onFreeze(dcx, dcy); // terminal (solver) or one-life freeze (World's WorldFx)
    }
  }

  /** Respawn at the sticky checkpoint (else PLAYER_START); reset per-life state. Gameplay only;
   *  the render reset + sprite re-sync + camera happen in `fx.onRespawn`. */
  respawn(): void {
    this.player.placeFeet(this.respawnCx, this.respawnFeetY);
    this.player.setForm(this.resolveActiveForm());
    this.buildSimEntities();
    // POLISH_03 #7: reset maxHearts to the level's authored startHearts every respawn —
    // buildSimEntities() re-spawns every placed heart-container fresh, so without this a
    // die-and-refarm loop could push maxHearts to the buffs.ts cap on any level with one
    // container. Every other per-life stat below is already reset the same way.
    this.maxHearts = this.level.meta.startHearts ?? 3;
    this.hearts = this.maxHearts;
    this.iframeTimer = 0;
    // POLISH_03 #8: the death mercy window is granted in die() AFTER this returns — respawn()
    // itself must stay grant-free (initial World.load call + manual resets are not deaths).
    this.invulnSteps = 0;
    this.score = 0;
    this.coins = 0;
    this.placedCoinsCollected = 0;
    this.enemiesKilled = 0;
    this.stepCount = 0;
    this.goalHint = '';
    this.rider = null;
    this.warpCooldownSteps = 0;
    this.pendingKill = false;
    this.pendingKillStarBlocks = false;
    this.latches.clear();
    this.cleared = false;
    this.dead = false;
    this.deathCause = null;
    this.respawnGen++;
    this.fx.onRespawn(this.respawnCx, this.respawnFeetY);
  }

  private hurtPlayer(): void {
    if (this.iframeTimer > 0 || this.invulnSteps > 0) return;
    this.hearts--;
    this.iframeTimer = this.mutators.hardcore ? 0 : IFRAME_SECONDS;
    this.fx.onHurt();
    if (this.hearts <= 0) this.die('enemy');
  }

  private healPlayer(n: number): void {
    const { hearts, wasted } = applyHeal({ hearts: this.hearts, maxHearts: this.maxHearts }, n);
    this.hearts = hearts;
    if (wasted) this.score += FULL_HEART_SCORE_BONUS;
  }

  private addMaxHeart(): void {
    const r = applyAddMaxHeart({ hearts: this.hearts, maxHearts: this.maxHearts });
    this.maxHearts = r.maxHearts;
    this.hearts = r.hearts;
  }

  private grantInvuln(steps: number): void {
    this.invulnSteps = grantInvuln(this.invulnSteps, steps);
  }

  private get progress(): ClearProgress {
    return {
      coinsCollected: this.coins, placedCoinsCollected: this.placedCoinsCollected, totalCoins: this.totalCoins,
      enemiesKilled: this.enemiesKilled, totalEnemies: this.totalEnemies,
    };
  }

  // ── Level statics + entity (re)build ─────────────────────────────────────────
  private buildStatics(): void {
    this.warpPoints = [];
    for (const e of this.level.entities) {
      const cx = e.x * this.ts + this.ts / 2;
      const feetY = (e.y + 1) * this.ts;
      if (e.type === EntityType.PLAYER_START) {
        this.respawnCx = cx; this.respawnFeetY = feetY;
        this.startCx = cx; this.startFeetY = feetY; // POLISH_03 #1: the true level start
      } else if (e.type === EntityType.GOAL_FLAG) {
        this.goalBox = { x: e.x * this.ts, y: (e.y - 1) * this.ts, w: this.ts, h: this.ts * 2 };
      } else if (e.type === EntityType.WARP || e.type === EntityType.DOOR) {
        const raw = e.props?.linkId;
        const id = typeof raw === 'number' ? raw : Number(raw ?? 0);
        this.warpPoints.push({ linkId: Number.isFinite(id) ? id : 0, cx, feetY });
      }
    }
  }

  private buildSimEntities(): void {
    this.entities = [];
    this.pendingSpawns = [];
    for (const le of this.level.entities) {
      const e = spawnEntity(le, this.ts);
      if (!e) continue;
      this.entities.push(e);
      if (e.spawnChildren) for (const child of e.spawnChildren()) this.entities.push(child);
    }
  }

  private spawnEntityAt(type: EntityType, cx: number, feetY: number, props?: Record<string, unknown>): Entity | null {
    const e = spawnEntityAtPx(type, cx, feetY, this.ts, props);
    // POLISH_03 #3/#4: stamp every RUNTIME emission (never a placed level.entities[] member) so
    // ctx.killEntity/Coin can tell a SPAWNER grunt / QBlock reward coin apart from an authored one.
    if (e) { e.spawned = true; this.pendingSpawns.push(e); }
    return e;
  }

  private flushSpawns(): void {
    if (this.pendingSpawns.length === 0) return;
    for (const e of this.pendingSpawns) { this.entities.push(e); this.fx.onSpawn(e); }
    this.pendingSpawns = [];
  }

  /**
   * POLISH_03 #5 (entity compaction sub-fix): implemented + unit-tested but gated OFF the live
   * step loop by default (SimOptions.compactEntities, default false — see its doc) — the golden
   * fixture (`Sim.golden.test.ts` / `golden.test.ts` → `golden.fixture.json`, GOLDEN GUARD-
   * protected) bakes in "a dead entity stays in `Sim.entities` forever" as asserted behaviour
   * (the `coins-goomba` case stomps a Goomba, then every later snapshot's `ents` still lists it
   * `alive:0`). Turning compaction on UNCONDITIONALLY changes that trajectory the instant it
   * fires, breaking the fixture on both the Sim-only and World-driven golden suites — and GOLDEN
   * GUARD forbids re-baking. World never opts in today (unedited under this MODE), so live-game
   * behaviour is unchanged; a caller that needs the perf win (long sessions, the server re-sim)
   * can pass `compactEntities: true` explicitly. See the final report for the recommended path
   * to eventually flipping the default.
   *
   * Sim.entities was append-only within a life (flushSpawns pushes; nothing ever removed a dead
   * entity), so a long run-and-gun session or a SPAWNER left running accretes unbounded dead
   * Projectiles/grunts — every step's four passes (update/solids/overlap/killEnemiesInBox) walk
   * the full array regardless. Swap-remove any entity that is BOTH `alive:false` AND no longer
   * `visible()` (a squash/fade window still running keeps it, so a mid-squash Goomba is never
   * yanked out from under its own render timer). Runs on an integer step-keyed cadence
   * ({@link COMPACT_CADENCE_STEPS}) — identical across every replay of the same inputs, so this
   * can never desync a recorded run. `this.rider` only ever references a LIVE entity (carryRider
   * bails when `!r.alive`), so a compacted entity is never a dangling rider ref.
   */
  private compactDeadEntities(): void {
    if (this.stepCount % COMPACT_CADENCE_STEPS !== 0) return;
    let write = 0;
    for (let read = 0; read < this.entities.length; read++) {
      const e = this.entities[read];
      if (!e.alive && !e.visible()) continue; // drop it
      this.entities[write++] = e;
    }
    this.entities.length = write;
  }

  /**
   * RUN-AND-GUN onShoot handler (Player fires → World spawns the bullet, mirrors onJump→sfx).
   * `dx`/`dy` is the NORMALIZED aim unit vector (from Player.handleShootInput). The bullet spawns
   * at the muzzle (in front of the body along the aim, mid-torso) through the deterministic
   * spawnEntityAt queue → flushed at end-of-step, so a recorded run re-emits identical projectiles.
   */
  private spawnBullet(dx: number, dy: number): void {
    const c = this.player.config;
    const b = this.player.body;
    const muzzleCx = b.x + b.w / 2 + dx * MUZZLE_OFFSET; // gun barrel in front of the body
    // Gun height, measured UP FROM THE FEET (not a fraction of the player's height) so a
    // horizontal shot travels at a standard ground enemy's body band regardless of player form —
    // an upper-torso muzzle (0.45·h) sailed over short enemies (playtest, 2026-07-01).
    const muzzleCy = b.y + b.h - 26;
    this.spawnEntityAt(EntityType.PROJECTILE, muzzleCx, muzzleCy + PROJECTILE_H / 2, {
      vx: dx * c.projectileSpeed, vy: dy * c.projectileSpeed, life: c.projectileLifeSteps,
    });
    this.fx.playSound('shoot', 0.4); // POLISH_01 #1 — dedicated player-blaster clip (was 'coin')
  }

  /**
   * RUN-AND-GUN entity-vs-entity kill seam (ctx.killEnemiesInBox). Kill every LIVE stomp-set
   * enemy overlapping `box` via the SAME ctx.killEntity path a stomp uses (reusing
   * combat.isAttackKillable — a bullet kills exactly what a stomp kills). Gated on
   * allowEnemyKills (= offense || verbs.shoot). Returns true on ≥1 hit (the bullet despawns).
   * A PROJECTILE ∉ the stomp set → a bullet never kills another bullet or itself.
   */
  private killEnemiesInBox(box: AABB): boolean {
    if (!this.allowEnemyKills) return false;
    let hit = false;
    for (const e of this.entities) {
      if (e.alive && isAttackKillable(e) && overlap(box, e.body)) {
        // HP-soak (step 3): an ARMORED with hp>1 is CHIPPED, not killed — the bullet still
        // "connects" (hit=true → it despawns) but the enemy survives until hp reaches 0. Enemies
        // with no hp (every prior type) fall straight through to killEntity, so the one-hit model
        // is byte-identical to before. Shared with the sword seam via chipOrKill.
        this.chipOrKill(e);
        hit = true;
      }
    }
    return hit;
  }

  /**
   * Shared hp-soak resolution for BOTH kill seams — the bullet path (killEnemiesInBox) and the
   * sword path (resolveAttackHitbox). An enemy with hp>1 is CHIPPED (hp--, onHit feedback) and
   * survives; every hp-less type routes straight to ctx.killEntity, byte-identical to the pre-soak
   * one-hit model. F1 (2026-07-01): the sword path used to killEntity outright, one-shotting
   * ARMORED/TURRET/SPAWNER — "only a bullet chips it" was a bullet-only accident, not a rule.
   * Both paths soak now.
   */
  private chipOrKill(e: Entity): void {
    if (e.hp !== undefined && e.hp > 1) {
      e.hp--;
      if (e.onHit) e.onHit(this.ctx);
    } else {
      this.ctx.killEntity(e);
    }
  }

  private checkGoal(): void {
    if (!this.goalBox) return;
    if (!overlap(this.player.body, this.goalBox)) return;
    if (conditionSatisfied(this.clearCondition, this.progress)) {
      this.cleared = true;
      this.fx.onClear();
    } else {
      this.goalHint = conditionHint(this.clearCondition, this.progress);
    }
  }

  // ── Verbatim mirrors of the World private passes (render calls → fx) ──────────
  private resolveSolidEntities(): void {
    const p = this.player.body;
    this.rider = null;
    for (const e of this.entities) {
      if (!e.alive || !e.solidBody) continue;
      const box = e.solidBody();
      if (!box) continue;
      const hit = resolvePlayerVsSolid(p, box);
      if (!hit) {
        if (this.player.grounded
            && p.x < box.x + box.w && p.x + p.w > box.x
            && Math.abs((p.y + p.h) - box.y) <= 2) {
          this.rider = e;
        }
        continue;
      }
      if (hit.side === 'left' || hit.side === 'right') {
        this.player.vx = 0;
      } else if (hit.side === 'top') {
        const landingVy = this.player.vy;
        if (this.player.vy > 0) this.player.vy = 0;
        this.player.grounded = true;
        this.rider = e;
        e.onStand?.(this.ctx, landingVy);
        if (this.player.vy < 0) { this.player.grounded = false; this.rider = null; }
      } else {
        if (this.player.vy < 0) {
          this.player.vy = 0;
          e.onBonk?.(this.ctx);
        }
      }
    }
  }

  /**
   * A live swing overlaps its target for the WHOLE active window (attackHitboxActiveSteps frames)
   * — without dedup a per-frame chip would drain an ARMORED's hp inside one swing (defeating F1).
   * `swordHits` records who THIS swing already chipped and clears the instant no strike is live, so
   * one swing = one chip per enemy — parity with one-bullet-one-chip. Kills of hp-less enemies are
   * unaffected: they go alive:false on frame 1 and never re-enter `kills`.
   */
  private readonly swordHits = new Set<Entity>();

  private resolveAttackHitbox(): void {
    if (!this.ctx.offenseEnabled) return;
    const hitbox = this.player.attackHitbox();
    if (!hitbox) { this.swordHits.clear(); return; } // swing over → reset the per-swing hit set
    const { kills, breaks, connected } = resolveAttack(hitbox, this.player.attackDir, this.entities);
    for (const e of kills) {
      if (this.swordHits.has(e)) continue; // already chipped by THIS swing
      this.swordHits.add(e);
      this.chipOrKill(e);
    }
    for (const e of breaks) this.ctx.killEntity(e); // CRACKEDWALL has no hp — always killEntity
    if (connected) {
      this.player.onAttackConnect();
      this.fx.playSound('slash', 0.45); // POLISH_01 #1 — dedicated sword-slash clip (was 'coin')
    }
    if (breaks.length) this.fx.playSound('break', 0.5); // POLISH_01 #1 — a CRACKEDWALL shatter
  }

  /**
   * POLISH_03 #6: dy now routes through `grid.collideY` exactly like dx routes through
   * `collideX`, instead of the previous raw `body.y += d.dy` — a vertical platform can no
   * longer shove the player INTO solid tiles with undefined collision behaviour. The crush
   * case (a rising platform pins the rider against a ceiling tile — `collideY` reports
   * `ceiling: true` for an upward `dy`) is Thwomp-style lethal: `ctx.killPlayer()` (never
   * `starBlocks` — a mechanical crush is genre-standard lethal through star, like a Thwomp).
   * A downward `dy` landing on a floor is the ordinary seat-on-ground case, not a crush.
   */
  private carryRider(dt: number): void {
    const r = this.rider;
    if (!r || !r.alive) return;
    if (r.solidBody && !r.solidBody()) return;
    const d = r.kinematicDelta?.();
    if (d) {
      if (d.dx !== 0) this.grid.collideX(this.player.body, d.dx);
      if (d.dy !== 0) {
        const res = this.grid.collideY(this.player.body, d.dy, false);
        if (d.dy < 0 && res.ceiling) this.ctx.killPlayer(); // pinned into a ceiling — crush
      }
    }
    const sv = r.surfaceVx?.();
    if (sv) this.grid.collideX(this.player.body, sv * dt);
  }

  private requestWarp(linkId: number, srcCx: number, srcFeetY: number): void {
    if (this.warpCooldownSteps > 0) return;
    const target = this.warpPoints.find(
      (w) => w.linkId === linkId && (w.cx !== srcCx || w.feetY !== srcFeetY),
    );
    if (!target) return;
    // POLISH_03 #9: teleportFeet (NOT placeFeet) — a warp repositions the body only, it must
    // never silently hand out a free dash/stamina refill, drop momentum beyond the teleport
    // itself, or force an AUTO-RUNNER to cruise right through a left-running corridor.
    this.player.teleportFeet(target.cx, target.feetY);
    this.warpCooldownSteps = WARP_COOLDOWN_STEPS;
    this.fx.onWarp();
  }

  private activateCheckpoint(cx: number, feetY: number): boolean {
    if (this.respawnCx === cx && this.respawnFeetY === feetY) return false;
    this.respawnCx = cx;
    this.respawnFeetY = feetY;
    return true;
  }

  // ── Read-only helpers the consumers (solver/proof) rely on ───────────────────
  /** Drive a whole input sequence; stops early on clear/death (terminal policy). */
  runInputs(inputs: readonly InputSnapshot[]): { cleared: boolean; dead: boolean; deathCause: string | null; steps: number } {
    for (const inp of inputs) {
      if (this.cleared || this.dead) break;
      this.step(1 / 60, inp);
    }
    return { cleared: this.cleared, dead: this.dead, deathCause: this.deathCause, steps: this.stepCount };
  }

  /** Quantized state key for a search's visited-set dedup (solver.ts). */
  stateKey(): string {
    const b = this.player.body;
    const q = (n: number, g: number): number => Math.round(n / g);
    const openLatches = [...this.latches.entries()].filter(([, v]) => v).map(([k]) => k).sort((a, z) => a - z).join('.');
    return [
      q(b.x, 8), q(b.y, 8), q(this.player.vx, 40), q(this.player.vy, 40),
      this.player.grounded ? 1 : 0, this.coins, this.enemiesKilled, openLatches,
    ].join(',');
  }

  /**
   * POLISH_03 #1 (P0): is the current respawn point still the level's PLAYER_START (i.e. NO sticky
   * checkpoint is active)? A recorded attempt that begins from a checkpoint is a mid-level SEGMENT,
   * not a full-level run — but PB/ghost/leaderboard/verify all re-sim from PLAYER_START, so the loop
   * owner banks/submits a replay ONLY while this is true (else the segment time files as an
   * unbeatable board run + desyncs the ghost + false-fails verify).
   */
  get atStartRespawn(): boolean {
    return this.respawnCx === this.startCx && this.respawnFeetY === this.startFeetY;
  }

  /** Count of currently-open latches — a solver heuristic rewards hitting switches. */
  get openLatchCount(): number {
    let n = 0;
    for (const v of this.latches.values()) if (v) n += 1;
    return n;
  }

  /** World-px center of the goal (for a solver heuristic). Null if the level has no flag. */
  goalCenter(): { x: number; y: number } | null {
    if (!this.goalBox) return null;
    return { x: this.goalBox.x + this.goalBox.w / 2, y: this.goalBox.y + this.goalBox.h / 2 };
  }
}

function overlap(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
